import * as angular from "angular";
import { DialogUtils } from "../../../helper/dialog.helper";
import { ToastUtils } from "../../../helper/toast.helper";
import { OrganisationService } from "../../../services/organisation.service";
import { AdminEnterService } from "../../../services/admin-enter.service";

interface OrgAccountRow {
    campaignid: number;
    campaignname: string;
    campaignregion: string;
    campaignstatusid: number | string;
    campaignstatusname: string;
    member: number | string;
}

/**
 * AV-18115: the accounts under one organisation, opened from the Accounts
 * count on the admin organisations list.
 *
 * The count on the row is only useful if it answers "which ones", so this
 * lists them. The open control joins the admin to the account first when
 * they are not already on it, so one link covers both jobs and the list
 * needs no separate join button.
 */
let AdminOrgAccountsDialogComponent = {
    selector: "vcAdminOrgAccountsDialog",
    templateUrl:
        "/angular/components/admin/dialogs/admin-org-accounts-dialog.component.html",
    bindings: {
        oid: "<",
        organisationName: "<",
    },
    controller: class AdminOrgAccountsDialogController {
        // * bindings for typescript compiler *
        public oid: number;
        public organisationName: string;
        // ************************************

        public loading = true;
        public error: string | null = null;
        public rows: OrgAccountRow[] = [];
        /** Counted once when the rows load; the template reads them every digest. */
        public live = 0;
        public cancelled = 0;
        public entering = false;

        private dialog: DialogUtils;
        private service: OrganisationService;
        private enter: AdminEnterService;
        private toast: ToastUtils;

        // Parameter names are the injector's lookup keys (implicit annotation).
        constructor(
            DialogUtils: DialogUtils,
            OrganisationService: OrganisationService,
            AdminEnterService: AdminEnterService,
            ToastUtils: ToastUtils
        ) {
            this.dialog = DialogUtils;
            this.service = OrganisationService;
            this.enter = AdminEnterService;
            this.toast = ToastUtils;
        }

        $onInit(): void {
            this.load();
        }

        private load(): void {
            this.loading = true;
            this.error = null;
            this.service.adminCampaigns(Number(this.oid)).then(
                (response: object) => {
                    const json = response["data"];
                    this.rows = (json && json.data) || [];
                    this.live = this.rows.filter(
                        (row) => !this.isCancelled(row)
                    ).length;
                    this.cancelled = this.rows.length - this.live;
                    this.loading = false;
                },
                () => {
                    this.error = "Could not load this organisation's accounts.";
                    this.loading = false;
                }
            );
        }

        isMember(row: OrgAccountRow): boolean {
            return row.member == 1 || row.member == "1";
        }

        /**
         * Cancelled accounts are listed last and greyed; the headline number
         * is the live ones, the same number the organisations list shows.
         */
        isCancelled(row: OrgAccountRow): boolean {
            return row.campaignstatusid == 3 || row.campaignstatusid == "3";
        }

        memberCount(): number {
            return this.rows.filter((row) => this.isMember(row)).length;
        }

        /** Join if needed, switch the session in, and open the dashboard. */
        open(row: OrgAccountRow): void {
            if (this.entering) {
                return;
            }
            this.entering = true;
            // enterAccount joins first when needed, so the row's member flag
            // has to travel with it or it would add a membership twice.
            this.enter.enterAccount(row).then(
                () => {
                    this.entering = false;
                },
                () => {
                    this.entering = false;
                    this.toast.error("Could not switch into the account");
                }
            );
        }

        close(): void {
            this.dialog.hide();
        }
    },
};

angular
    .module("admin")
    .component(
        AdminOrgAccountsDialogComponent.selector,
        AdminOrgAccountsDialogComponent
    );
